import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Drawer,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import {
    AddRounded,
    DeleteOutlineRounded,
    EditRounded,
    HistoryRounded,
    PersonAddAltRounded,
    VisibilityRounded,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import VersionHistoryDrawer from "../components/VersionHistoryDrawer";
import { Button, IconButton } from "../components/Buttons";
import {
    createOrgRole,
    deleteOrgRole,
    getPermissionCatalog,
    inviteOrgUser,
    listOrgRoles,
    updateOrgRole,
} from "../api";
import { formatDate } from "../utils/format";

const apiError = (err, fallback) =>
    err.response?.data?.error || err.response?.data?.message || fallback;

/* ------------------------------------------------------------------ */
/* Permission matrix editor (create / edit / view a role)              */
/* ------------------------------------------------------------------ */

function buildMatrix(catalog, role) {
    const matrix = {};
    (catalog?.resources ?? []).forEach((res) => {
        const existing = role?.permissions?.find(
            (p) => p.resource === res.key,
        );
        matrix[res.key] = {
            actions: existing?.actions ?? [],
            scope: existing?.scope ?? "own",
            fieldDeny: existing?.fieldDeny ?? [],
        };
    });
    return matrix;
}

function RoleEditor({ open, role, catalog, onClose, onSaved }) {
    const readOnly = !!role?.isSystem;
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [matrix, setMatrix] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setName(role?.name ?? "");
        setDescription(role?.description ?? "");
        setMatrix(buildMatrix(catalog, role));
    }, [open, role, catalog]);

    const scopes = catalog?.scopes ?? [];

    function toggleAction(resourceKey, actionKey) {
        if (readOnly) return;
        setMatrix((prev) => {
            const row = prev[resourceKey];
            const has = row.actions.includes(actionKey);
            return {
                ...prev,
                [resourceKey]: {
                    ...row,
                    actions: has
                        ? row.actions.filter((a) => a !== actionKey)
                        : [...row.actions, actionKey],
                },
            };
        });
    }

    function setScope(resourceKey, scope) {
        if (readOnly) return;
        setMatrix((prev) => ({
            ...prev,
            [resourceKey]: { ...prev[resourceKey], scope },
        }));
    }

    async function handleSave() {
        if (!name.trim()) {
            toast.error("Give the role a name");
            return;
        }
        const permissions = Object.entries(matrix)
            .filter(([, row]) => row.actions.length > 0)
            .map(([resource, row]) => {
                const perm = {
                    resource,
                    actions: row.actions,
                    scope: row.scope,
                };
                if (row.fieldDeny?.length) perm.fieldDeny = row.fieldDeny;
                return perm;
            });

        if (permissions.length === 0) {
            toast.error("Grant at least one permission");
            return;
        }

        setSaving(true);
        try {
            const body = {
                name: name.trim(),
                description: description.trim() || undefined,
                permissions,
            };
            if (role?.id) {
                await updateOrgRole(role.id, body);
                toast.success(`Role "${body.name}" updated`);
            } else {
                await createOrgRole(body);
                toast.success(`Role "${body.name}" created`);
            }
            onSaved();
        } catch (err) {
            toast.error(apiError(err, "Could not save the role"));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: "100%", md: 760 },
                    background: "var(--bg)",
                },
            }}
        >
            <Stack sx={{ height: "100%" }}>
                <Stack
                    sx={{
                        p: "24px 28px 20px",
                        borderBottom: "1px solid var(--border)",
                        gap: 0.5,
                        background: "var(--bg-secondary)",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 11,
                            letterSpacing: "2px",
                            fontWeight: 700,
                            color: "var(--text-tertiary)",
                        }}
                    >
                        ROLE BUILDER
                    </Typography>
                    <Stack
                        sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 1.5,
                        }}
                    >
                        <Typography variant="h5">
                            {readOnly
                                ? role?.name
                                : role
                                  ? "Edit role"
                                  : "New role"}
                        </Typography>
                        {readOnly && (
                            <Chip
                                size="small"
                                label="System — view only"
                                sx={{
                                    background: "var(--bg-tint-soft)",
                                    border: "1px solid var(--border-mid)",
                                    color: "var(--text-secondary)",
                                }}
                            />
                        )}
                    </Stack>
                </Stack>

                <Stack sx={{ flex: 1, overflowY: "auto", p: "24px 28px", gap: 3 }}>
                    <Stack
                        sx={{
                            flexDirection: { xs: "column", sm: "row" },
                            gap: 2,
                        }}
                    >
                        <TextField
                            label="Role name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            disabled={readOnly}
                            sx={{ flex: 1 }}
                        />
                        <TextField
                            label="Description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            disabled={readOnly}
                            sx={{ flex: 2 }}
                        />
                    </Stack>

                    <Stack sx={{ gap: 1.5 }}>
                        <Typography
                            sx={{
                                fontSize: 11,
                                letterSpacing: "2px",
                                fontWeight: 700,
                                color: "var(--text-tertiary)",
                            }}
                        >
                            PERMISSIONS
                        </Typography>

                        <TableContainer
                            sx={{ background: "var(--bg-secondary)" }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Resource</TableCell>
                                        <TableCell>Actions</TableCell>
                                        <TableCell sx={{ width: 180 }}>
                                            Scope
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(catalog?.resources ?? []).map((res) => {
                                        const row = matrix[res.key] ?? {
                                            actions: [],
                                            scope: "own",
                                        };
                                        const anyChecked =
                                            row.actions.length > 0;
                                        return (
                                            <TableRow key={res.key}>
                                                <TableCell
                                                    sx={{
                                                        fontWeight: 600,
                                                        whiteSpace: "nowrap",
                                                        verticalAlign: "top",
                                                        pt: 1.5,
                                                    }}
                                                >
                                                    {res.label}
                                                </TableCell>
                                                <TableCell>
                                                    <Stack
                                                        sx={{
                                                            flexDirection:
                                                                "row",
                                                            flexWrap: "wrap",
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        {(
                                                            res.actions ?? []
                                                        ).map((action) => {
                                                            const checked =
                                                                row.actions.includes(
                                                                    action.key,
                                                                );
                                                            return (
                                                                <Stack
                                                                    key={
                                                                        action.key
                                                                    }
                                                                    onClick={() =>
                                                                        toggleAction(
                                                                            res.key,
                                                                            action.key,
                                                                        )
                                                                    }
                                                                    sx={{
                                                                        flexDirection:
                                                                            "row",
                                                                        alignItems:
                                                                            "center",
                                                                        gap: 0.25,
                                                                        pr: 1,
                                                                        cursor: readOnly
                                                                            ? "default"
                                                                            : "pointer",
                                                                        userSelect:
                                                                            "none",
                                                                    }}
                                                                >
                                                                    <Checkbox
                                                                        size="small"
                                                                        checked={
                                                                            checked
                                                                        }
                                                                        disabled={
                                                                            readOnly
                                                                        }
                                                                        sx={{
                                                                            p: 0.5,
                                                                            color: "var(--border-strong)",
                                                                            "&.Mui-checked":
                                                                                {
                                                                                    color: "var(--accent)",
                                                                                },
                                                                        }}
                                                                    />
                                                                    <Typography
                                                                        sx={{
                                                                            fontSize: 13.5,
                                                                            color: checked
                                                                                ? "var(--text)"
                                                                                : "var(--text-secondary)",
                                                                            fontWeight:
                                                                                checked
                                                                                    ? 600
                                                                                    : 400,
                                                                        }}
                                                                    >
                                                                        {
                                                                            action.label
                                                                        }
                                                                    </Typography>
                                                                </Stack>
                                                            );
                                                        })}
                                                    </Stack>
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        verticalAlign: "top",
                                                        pt: 1,
                                                    }}
                                                >
                                                    <Select
                                                        size="small"
                                                        fullWidth
                                                        value={row.scope}
                                                        disabled={
                                                            readOnly ||
                                                            !anyChecked
                                                        }
                                                        onChange={(e) =>
                                                            setScope(
                                                                res.key,
                                                                e.target.value,
                                                            )
                                                        }
                                                        sx={{ fontSize: 13.5 }}
                                                    >
                                                        {scopes.map((s) => (
                                                            <MenuItem
                                                                key={s.key}
                                                                value={s.key}
                                                                sx={{
                                                                    fontSize: 13.5,
                                                                }}
                                                            >
                                                                {s.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Stack>
                </Stack>

                <Stack
                    sx={{
                        flexDirection: "row",
                        justifyContent: "flex-end",
                        gap: 1.5,
                        p: "16px 28px",
                        borderTop: "1px solid var(--border)",
                        background: "var(--bg-secondary)",
                    }}
                >
                    <Button variant="soft-outlined" onClick={onClose}>
                        {readOnly ? "Close" : "Cancel"}
                    </Button>
                    {!readOnly && (
                        <Button
                            variant="contained"
                            color="primary"
                            loading={saving}
                            onClick={handleSave}
                        >
                            {role ? "Save changes" : "Create role"}
                        </Button>
                    )}
                </Stack>
            </Stack>
        </Drawer>
    );
}

/* ------------------------------------------------------------------ */
/* Invite user dialog                                                  */
/* ------------------------------------------------------------------ */

function InviteDialog({ open, roles, onClose }) {
    const [form, setForm] = useState({
        firstName: "",
        lastName: "",
        email: "",
        orgRoleId: "",
    });
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (open) {
            setForm({ firstName: "", lastName: "", email: "", orgRoleId: "" });
        }
    }, [open]);

    const set = (key) => (e) =>
        setForm((prev) => ({ ...prev, [key]: e.target.value }));

    async function handleInvite() {
        if (!form.firstName.trim() || !form.email.trim() || !form.orgRoleId) {
            toast.error("First name, email and role are required");
            return;
        }
        setSending(true);
        try {
            const body = {
                firstName: form.firstName.trim(),
                email: form.email.trim(),
                orgRoleId: form.orgRoleId,
            };
            if (form.lastName.trim()) body.lastName = form.lastName.trim();
            const { data } = await inviteOrgUser(body);
            toast.success(data?.message || "Invitation sent");
            onClose();
        } catch (err) {
            toast.error(apiError(err, "Could not send the invite"));
        } finally {
            setSending(false);
        }
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontFamily: "'Newsreader', Georgia, serif" }}>
                Invite a teammate
            </DialogTitle>
            <DialogContent>
                <Stack sx={{ gap: 2, pt: 1 }}>
                    <Stack sx={{ flexDirection: "row", gap: 2 }}>
                        <TextField
                            label="First name"
                            value={form.firstName}
                            onChange={set("firstName")}
                            required
                            fullWidth
                            autoFocus
                        />
                        <TextField
                            label="Last name"
                            value={form.lastName}
                            onChange={set("lastName")}
                            fullWidth
                        />
                    </Stack>
                    <TextField
                        label="Work email"
                        type="email"
                        value={form.email}
                        onChange={set("email")}
                        required
                        fullWidth
                    />
                    <TextField
                        label="Role"
                        select
                        value={form.orgRoleId}
                        onChange={set("orgRoleId")}
                        required
                        fullWidth
                    >
                        {roles.map((r) => (
                            <MenuItem key={r.id} value={r.id}>
                                {r.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button variant="soft-outlined" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    loading={sending}
                    onClick={handleInvite}
                >
                    Send invite
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function AdminRoles() {
    const [roles, setRoles] = useState([]);
    const [catalog, setCatalog] = useState(null);
    const [loading, setLoading] = useState(false);
    const [editor, setEditor] = useState({ open: false, role: null });
    const [deleting, setDeleting] = useState(null);
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [historyFor, setHistoryFor] = useState(null);

    const loadRoles = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await listOrgRoles();
            setRoles(Array.isArray(data) ? data : (data?.data ?? []));
        } catch (err) {
            toast.error(apiError(err, "Failed to load roles"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadRoles();
        (async () => {
            try {
                const { data } = await getPermissionCatalog();
                setCatalog(data);
            } catch (err) {
                toast.error(
                    apiError(err, "Failed to load the permission catalog"),
                );
            }
        })();
    }, [loadRoles]);

    async function handleDelete() {
        if (!deleting) return;
        setDeleteBusy(true);
        try {
            await deleteOrgRole(deleting.id);
            toast.success(`Role "${deleting.name}" deleted`);
            setDeleting(null);
            loadRoles();
        } catch (err) {
            toast.error(apiError(err, "Could not delete the role"));
        } finally {
            setDeleteBusy(false);
        }
    }

    const columns = useMemo(
        () => [
            {
                key: "name",
                label: "Role",
                render: (row) => (
                    <Stack
                        sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                            {row.name}
                        </Typography>
                        {row.isSystem && (
                            <Chip
                                size="small"
                                label="System"
                                sx={{
                                    background: "var(--bg-tint-soft)",
                                    border: "1px solid var(--border-mid)",
                                    color: "var(--text-secondary)",
                                    height: 20,
                                }}
                            />
                        )}
                    </Stack>
                ),
            },
            {
                key: "description",
                label: "Description",
                render: (row) => row.description || "—",
            },
            {
                key: "userCount",
                label: "Users",
                align: "right",
                render: (row) => row.userCount ?? 0,
            },
            {
                key: "createdAt",
                label: "Created",
                render: (row) => formatDate(row.createdAt),
            },
            {
                key: "actions",
                label: "",
                align: "right",
                render: (row) => (
                    <Stack
                        sx={{
                            flexDirection: "row",
                            justifyContent: "flex-end",
                            gap: 0.5,
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <IconButton
                            size="32px"
                            variant="text"
                            title="History"
                            onClick={() => setHistoryFor(row)}
                        >
                            <HistoryRounded sx={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton
                            size="32px"
                            variant="text"
                            onClick={() => setEditor({ open: true, role: row })}
                            title={row.isSystem ? "View" : "Edit"}
                        >
                            {row.isSystem ? (
                                <VisibilityRounded sx={{ fontSize: 18 }} />
                            ) : (
                                <EditRounded sx={{ fontSize: 18 }} />
                            )}
                        </IconButton>
                        {!row.isSystem && (
                            <IconButton
                                size="32px"
                                variant="text"
                                color="error"
                                onClick={() => setDeleting(row)}
                                title="Delete"
                            >
                                <DeleteOutlineRounded sx={{ fontSize: 18 }} />
                            </IconButton>
                        )}
                    </Stack>
                ),
            },
        ],
        [],
    );

    return (
        <Stack>
            <PageHeader
                title="Roles"
                subtitle="Design who sees and does what across your workspace"
                action={
                    <Stack sx={{ flexDirection: "row", gap: 1.5 }}>
                        <Button
                            variant="soft-outlined"
                            startIcon={<PersonAddAltRounded />}
                            onClick={() => setInviteOpen(true)}
                        >
                            Invite user
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddRounded />}
                            onClick={() =>
                                setEditor({ open: true, role: null })
                            }
                        >
                            New role
                        </Button>
                    </Stack>
                }
            />

            <DataTable
                columns={columns}
                rows={roles}
                loading={loading}
                emptyMessage="No roles yet — create the first one"
                onRowClick={(row) => setEditor({ open: true, role: row })}
            />

            <RoleEditor
                open={editor.open}
                role={editor.role}
                catalog={catalog}
                onClose={() => setEditor({ open: false, role: null })}
                onSaved={() => {
                    setEditor({ open: false, role: null });
                    loadRoles();
                }}
            />

            <InviteDialog
                open={inviteOpen}
                roles={roles}
                onClose={() => setInviteOpen(false)}
            />

            <VersionHistoryDrawer
                open={!!historyFor}
                onClose={() => setHistoryFor(null)}
                kind="role"
                entityId={historyFor?.id}
                title={historyFor?.name ?? "Role"}
            />

            <Dialog open={!!deleting} onClose={() => setDeleting(null)}>
                <DialogTitle
                    sx={{ fontFamily: "'Newsreader', Georgia, serif" }}
                >
                    Delete this role?
                </DialogTitle>
                <DialogContent>
                    <Typography
                        sx={{ fontSize: 14.5, color: "var(--text-secondary)" }}
                    >
                        &#8220;{deleting?.name}&#8221; will be removed
                        permanently. Users must be moved to another role first.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button
                        variant="soft-outlined"
                        onClick={() => setDeleting(null)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        loading={deleteBusy}
                        onClick={handleDelete}
                    >
                        Delete role
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
